<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header">
          <div class="title-container" v-if="$can('read_all-kpi_reports')">
            <h3 class="title log_time_head">
              KPI report<template v-if="reports">: {{getDateInterval}}</template>
              <div v-if="isDataPageLoading" class="spinner-border spinner-theme_default" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </h3>
            <div class="title-container__controls">
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
            </div>

          </div>

          <Filters
            v-if="$can('read_all-kpi_reports')"
            v-show="isShowFilter"
            textButtonSubmit="Get Report"
            :data="filters"
            :isLoading="isDataPageLoading"
            showClearCache
            @submit="getReports"
            @clearCache="clearCache"
          />

          <FullscreenTabs v-if="reports">
            <template v-slot:tabsControls>
              <div class="d-flex">
                <div class="table-tab-item" v-for="tab in tabs" :key="tab.name">
                  <b-button
                    class="button text-capitalize"
                    :variant="tab.name === currentTab.name ? 'info' : 'secondary-outline'"
                    @click="setCurrentTab(tab)"
                  >
                    {{ tab.alias }}
                  </b-button>
                </div>
              </div>
            </template>
            <template v-slot:tabsContent>
              <div>
                <div
                  v-show="currentTab.name === 'people'"
                  class="table__theme-default"
                >
                  <TableForPage
                    ref="tablePeople"
                    style="margin-bottom: 0;"
                    :primaryKeyForTable="primaryKeyForTable"
                    :tableData="getReportsPeople"
                    :fields="getFieldsPeople"
                    :slots="getSlotsPeopleReports"
                    :isFullScreen="false"
                    :perPage="30"
                    :tableDescription="columnDescription"
                    showTopControls
                  >
                    <template v-slot:tableControls>
                      <div class="table-controls-list">
                        <div class="table-controls-item filter-datepicker">
                            <b-button
                              variant="light"
                              :disabled="disablePrevMonth"
                              @click.prevent="changeMonth('prev')"
                            >
                              ‹
                            </b-button>
                            <Datepicker
                              :bootstrap-styling="true"
                              :value="_toIsoStringTime(tabs.people.date.to)"
                              :format="config.dateFormatDatePicker"
                              :disabledDates="{ to: new Date(fromDateToUTC(tabs.people.date.from, 'YYYY-MM')), from: new Date() }"
                              minimum-view="month"
                              @selected="selectMonth"
                            />
                            <b-button
                              variant="light"
                              :disabled="disableNextMonth"
                              @click.prevent="changeMonth('next')"
                            >
                              ›
                            </b-button>
                        </div>
                        <div class="table-controls-item">
                            <ButtonSwitch
                              v-model="tabs.people.sinceStartMonth"
                              firstText="Month"
                              secondText="Since start"
                            />
                        </div>
                        <div class="table-controls-item">
                          <RoleFilter :data="tabs.people.filters" @selected="changeFilter" />
                        </div>
                      </div>
                    </template>
                    <template :slot="`${getSummaryKey}.kpi`" slot-scope="data" class="for-test">
                      <strong
                        class="cursor-pointer"
                        :title="'by logged time: ' + data['item'][getSummaryKey]['kpiBySpentTime'] + '% | by actual spent time:' + data['item'][getSummaryKey]['kpiByActualSpentTime'] + '%'"
                      >
                        {{data['item'][getSummaryKey]['kpi']}}% ({{data['item'][getSummaryKey]['kpiBySpentTime']}}%)
                      </strong>
                    </template>
                    <template :slot="`${getSummaryKey}.billableHoursRatio`" slot-scope="data" class="for-test">
                      <strong>
                        {{data['item'][getSummaryKey]['billableHoursRatio']}}% ({{data['item'][getSummaryKey]['billableHours']}})
                      </strong>
                    </template>
                    <template :slot="`${getSummaryKey}.spentBench`" slot-scope="data" class="for-test">
                      <strong>
                        {{data['item'][getSummaryKey]['spentBench']}} ({{data['item'][getSummaryKey]['spentWaitingForTasks']}})
                      </strong>
                    </template>
                    <template v-slot:row="slotProps">
                      <NestedTable
                        style="margin-bottom: 0;"
                        :items="getReportDetailsByUser(slotProps.row.item, 'jira_user_name')"
                        :fields="fieldsIssues"
                        :slots="slotsReportDetails"
                      >
                        <template slot="keyWorklogs" slot-scope="data">
                          <a :href="`https://giraffesoftware.atlassian.net/browse/${data.item.keyWorklogs}`" target="_blank">
                            {{data.item.keyWorklogs}}
                          </a>
                        </template>
                        <template slot="keyOutsource" slot-scope="data">
                          <a :href="`https://giraffesoftware.atlassian.net/browse/${data.item.keyOutsource}`" target="_blank">
                            {{data.item.keyOutsource}}
                          </a>
                        </template>
                      </NestedTable>
                    </template>
                  </TableForPage>
                </div>
                <div
                  v-show="currentTab.name === 'projects'"
                  class="table__theme-default"
                >
                  <TableForPage
                    ref="tableProjects"
                    style="margin-bottom: 0;"
                    :primaryKeyForTable="primaryKeyForTable"
                    :tableData="getReportsProjects"
                    :fields="getFieldsProjects"
                    :slots="getSlotsProjectsReports"
                    :perPage="30"
                    :isFullScreen="false"
                    :tableDescription="columnDescription"
                    showTopControls
                  >
                    <template v-slot:tableControls>
                      <div class="table-controls-list ">
                        <div class="table-controls-item filter-datepicker">
                            <b-button
                              variant="light"
                              :disabled="disablePrevMonth"
                              @click.prevent="changeMonth('prev')"
                            >
                              ‹
                            </b-button>
                            <Datepicker
                              :bootstrap-styling="true"
                              :value="_toIsoStringTime(tabs.projects.date.to)"
                              :format="config.dateFormatDatePicker"
                              :disabledDates="{ to: new Date(fromDateToUTC(tabs.projects.date.from, 'YYYY-MM')), from: new Date() }"
                              minimum-view="month"
                              @selected="selectMonth"
                            />
                            <b-button
                              variant="light"
                              :disabled="disableNextMonth"
                              @click.prevent="changeMonth('next')"
                            >
                              ›
                            </b-button>
                        </div>
                        <div class="table-controls-item">
                          <ButtonSwitch
                            v-model="tabs.projects.sinceStartMonth"
                            firstText="Month"
                            secondText="Since start"
                          />
                        </div>
                      </div>
                    </template>
                    <template :slot="`${getSummaryKey}.kpi`" slot-scope="data" class="for-test">
                      <strong>{{data['item'][getSummaryKey]['kpi']}}%</strong>
                    </template>

                    <template :slot="`${getSummaryKey}.billableHoursRatio`" slot-scope="data" class="for-test">
                      <strong>{{data['item'][getSummaryKey]['billableHoursRatio']}}% ({{data['item'][getSummaryKey]['billableHours']}})</strong>
                    </template>

                    <template :slot="`${getSummaryKey}.paidHoursByPeriod`" slot-scope="data" class="for-test">
                      <strong>{{data['item'][getSummaryKey]['paidHoursByPeriod']}}</strong>
                    </template>

                    <template slot="name" slot-scope="data" class="for-test">
                      <a :href="`https://giraffesoftware.atlassian.net/browse/${getProjectDashboardKey(data.item)}`" target="_blank">{{data.item.name}}</a>
                    </template>

                    <template v-slot:row="slotProps">
                      <NestedTable
                        style="margin-bottom: 0;"
                        :items="getReportDetailsByUser(slotProps.row.item, 'key')"
                        :fields="fieldsIssues"
                        :slots="slotsReportDetails"
                      >
                        <template slot="keyWorklogs" slot-scope="data">
                          <a :href="`https://giraffesoftware.atlassian.net/browse/${data.item.keyWorklogs}`" target="_blank">
                            {{data.item.keyWorklogs}}
                          </a>
                        </template>
                        <template slot="keyOutsource" slot-scope="data">
                          <span>{{data.item.keyOutsource}}</span>
                        </template>
                      </NestedTable>
                    </template>

                  </TableForPage>
                </div>
              </div>
            </template>
          </FullscreenTabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Filters from '@components/Filter/Filter'
import RoleFilter from '@components/RoleFilter'
import NestedTable from '@tables/NestedTable'
import TableForPage from '@tables/TableForPage'
import ButtonSwitch from '@forms/ButtonSwitch'
import FullscreenTabs from '@components/Fullscreen/FullscreenTabs'
import Moment from 'moment'
import Datepicker from 'vuejs-datepicker'

const currentMonth = Moment().format('YYYY-MM')

export default {
  name: 'KPI-Report',
  components: {
    Filters,
    Datepicker,
    RoleFilter,
    NestedTable,
    TableForPage,
    ButtonSwitch,
    FullscreenTabs
  },
  data () {
    return {
      isShowFilter: true,
      filters: {
        'projects': {
          type: 'multiselect',
          name: 'projects',
          label: 'Select projects',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-4'
        },
        'users': {
          type: 'multiselect',
          name: 'users',
          label: 'Select users',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-4'
        },
        'unitManagers': {
          type: 'multiselect',
          name: 'unitManagers',
          nameItem: 'project_id',
          label: 'Select unit manager',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-4'
        }
      },
      columnDescription: [
        '<strong>KPI</strong> = (done estimate + spent outstaff) / max(spent outsource + spent outstaff, actual spent outsource + spent outstaff)',
        '<strong>ProfitByEstimation</strong> = done estimate - (actual spent outsource * 0.8 * (1 - 0.12))',
        '<strong>BillableHours</strong> = outsource billable hours (from user stories budget) + outstaff hours',
        '<strong>BillableHours %</strong> = BillableHours / actual work time by user (all hours - sickday / holidays and vacations adn not working hours)',
        '<strong>PH</strong> = Paid hours by period',
        '<strong>Bench</strong> = hours logged into tasks with "bench" label, (WaitingForTasks - hours logged into tasks with "waiting_for_tasks" label)'
      ],

      primaryKeyForTable: 'key',
      reports: null,
      doneIssues: [],
      worklogsOutsource: null,
      peopleAndProjectsInfo: null,
      config: {
        dateFormat: 'YYYY-MM',
        dateFormatDatePicker: 'yyyy-MM'
      },
      currentTab: { name: 'projects', alias: 'Projects' },
      tabs: {
        projects: {
          name: 'projects',
          alias: 'Projects',
          date: { from: null, to: currentMonth },
          sinceStartMonth: false
        },
        people: {
          name: 'people',
          alias: 'People',
          date: { from: null, to: currentMonth },
          sinceStartMonth: false,
          filters: [
            {
              display_name: 'Business Analyst',
              name: 'business_analyst'
            },
            {
              display_name: 'Designer',
              name: 'designer'
            },
            {
              display_name: 'Designer',
              name: 'designer'
            },
            {
              display_name: 'Project Manager',
              name: 'project_manager'
            },
            {
              display_name: 'Qa',
              name: 'qa'
            },
            {
              display_name: 'Developer',
              name: 'developer'
            }
          ]
        }
      },
      slotsReportDetails: {
        keyWorklogs: {
          name: 'keyWorklogs'
        },
        keyOutsource: {
          name: 'keyOutsource'
        }
      },
      isDataPageLoading: false,

      fieldsIssues: [
        {
          key: 'keyWorklogs',
          label: 'key',
          sortable: true
        },
        {
          key: 'valueWorklogs',
          label: 'Estimate time',
          sortable: true
        },
        {
          key: 'keyOutsource',
          label: 'key',
          sortable: true
        },
        {
          key: 'valueOutsource',
          label: 'Spent time',
          sortable: true
        }
      ],
      fieldsWorklogs: [
        {
          key: 'key',
          sortable: true
        },
        {
          key: 'value',
          label: 'Spent time',
          sortable: true
        },
        {
          key: 'worklogCreatedAt',
          sortable: true
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUserRole: 'getCurrentUserRole',
      getCurrentUserName: 'getCurrentUserName',
      getAllUsers: 'getAllUsers',
      getProjects: 'projects/getProjects',
      getUnitManagers: 'users/getUnitManagers'
    }),
    getFieldsPeople () {
      return [
        {
          key: 'unitManagerName',
          label: 'Manager',
          sortable: true
        },
        {
          key: 'name',
          label: 'Specialist',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.kpi`,
          label: 'KPI, %',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.billableHoursRatio`,
          label: 'BH, %',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.profitByEstimation`,
          label: 'PBE',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.estimate`,
          label: 'ET',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.actualSpentOutsource`,
          label: 'Act. outsource ST',
          class: 'column-act-outsource',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentOutsource`,
          label: 'Outsource ST',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentOutstaff`,
          label: 'Outstаff ST',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentBench`,
          label: 'Bench',
          sortable: true
        },
        {
          key: 'more',
          label: 'More',
          class: 'column-more' + (this.getSummaryKey === 'summary_since_start' ? ' disabled-more' : '')
        }
      ]
    },
    getSlotsPeopleReports () {
      return {
        'kpi': {
          name: `${this.getSummaryKey}.kpi`
        },
        'billableHoursRatio': {
          name: `${this.getSummaryKey}.billableHoursRatio`
        },
        'spentBench': {
          name: `${this.getSummaryKey}.spentBench`
        }
      }
    },
    getSlotsProjectsReports () {
      return {
        'kpi': {
          name: `${this.getSummaryKey}.kpi`
        },
        'billableHoursRatio': {
          name: `${this.getSummaryKey}.billableHoursRatio`
        },
        'paidHoursByPeriod': {
          name: `${this.getSummaryKey}.paidHoursByPeriod`
        },
        'name': {
          name: 'name'
        }
      }
    },
    getFieldsProjects () {
      return [
        {
          key: 'name',
          label: 'Name',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.kpi`,
          label: 'KPI, %',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.billableHoursRatio`,
          label: 'BH, %',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.paidHoursByPeriod`,
          label: 'PH',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.estimate`,
          label: 'Estimate time',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentOutsource`,
          label: 'Outsource ST',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentOutstaff`,
          label: 'Outstaff ST',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentNoEstimation`,
          label: 'No Estimation ST',
          sortable: true
        },
        {
          key: 'more',
          label: 'More',
          class: 'column-more' + (this.getSummaryKey === 'summary_since_start' ? ' disabled-more' : '')
        }
      ]
    },
    getSummaryKey () {
      return this.getCurrentTab.sinceStartMonth ? 'summary_since_start' : 'summary'
    },
    getDateInterval () {
      const date = this.getCurrentTab.date
      return `${date.from} - ${date.to}`
    },
    getCurrentTab () {
      return this.tabs[this.currentTab.name]
    },
    getReportsProjects () {
      return this.getReportsByTabsAndMonth('projects')
      // let reportWithPersonInfo = []
      // const currentTab = this.tabs.projects
      // const reportByTabAndMonth = this.reports[currentTab.name][currentTab.date.to]
      //
      // for (let key in reportByTabAndMonth) {
      //   let item = {
      //     key,
      //     ...reportByTabAndMonth[key],
      //     ...this.peopleAndProjectsInfo[currentTab.name][key]
      //   }
      //   if (item.name === undefined) {
      //     item.name = `${key} project`
      //   }
      //   reportWithPersonInfo.push(item)
      // }
      //
      // return reportWithPersonInfo
    },
    getReportsPeople () {
      return this.getReportsByTabsAndMonth('people')
      // let reportWithPersonInfo = []
      // const currentTab = this.tabs.people
      // const reportByTabAndMonth = this.reports[currentTab.name][currentTab.date.to]
      //
      // for (let key in reportByTabAndMonth) {
      //   let item = {
      //     key,
      //     ...reportByTabAndMonth[key],
      //     ...this.peopleAndProjectsInfo[currentTab.name][key]
      //   }
      //   if (item.name === undefined) {
      //     item.name = `${key} project`
      //   }
      //   reportWithPersonInfo.push(item)
      // }
      //
      // if (currentTab.filters && currentTab.filters[0].name !== 'all') {
      //   reportWithPersonInfo = this.reportFilterByRole(reportWithPersonInfo)
      // }
      //
      // return reportWithPersonInfo
    },
    disableNextMonth () {
      return this.getCurrentTab.date.to === currentMonth
    },
    disablePrevMonth () {
      return this.getCurrentTab.date.from === this.getCurrentTab.date.to
    }
  },
  created () {
    this._toggleDataPageLoading(async () => {
      await this.fetchAllData()
    })
  },
  methods: {
    getReportsByTabsAndMonth (tabName) {
      let reportWithPersonInfo = []
      const currentTab = this.tabs[tabName]
      const reportByTabAndMonth = this.reports[currentTab.name][currentTab.date.to]

      for (let key in reportByTabAndMonth) {
        if (reportByTabAndMonth[key][this.getSummaryKey]) {
          let item = {
            key,
            ...reportByTabAndMonth[key],
            ...this.peopleAndProjectsInfo[currentTab.name][key]
          }
          if (item.name === undefined) {
            item.name = `${key} project`
          }
          reportWithPersonInfo.push(item)
        }
      }

      if (currentTab.filters && currentTab.filters[0].name !== 'all') {
        reportWithPersonInfo = this.reportFilterByRole(reportWithPersonInfo, currentTab.filters)
      }

      return reportWithPersonInfo
    },
    setCurrentTab (tab) {
      this.currentTab = tab
    },
    toggleShowFilter () {
      this.isShowFilter = !this.isShowFilter
    },
    changeFilter (filters) {
      this.tabs[this.currentTab.name].filters = filters
    },
    selectMonth (data) {
      this.tabs[this.currentTab.name].date.to = this._toLocalTime(data, this.config.dateFormat)
    },
    changeMonth (type) {
      const currentMonth = Moment(this.tabs[this.currentTab.name].date.to, this.config.dateFormat)
      let newMonth

      if (type === 'next') {
        newMonth = currentMonth.add(1, 'months').format(this.config.dateFormat)
      } else {
        newMonth = currentMonth.subtract(1, 'months').format(this.config.dateFormat)
      }

      this.tabs[this.currentTab.name].date.to = newMonth
    },
    resetPagination () {
      this.$refs['tablePeople'].resetPagination()
      this.$refs['tableProjects'].resetPagination()
    },
    getReports (data) {
      const newData = this.prepareDataForGetReports(data)

      return this._toggleDataPageLoading(async () => {
        await this.$api.kpi.getKpi(newData).then(response => {
          const dateFrom = Object.keys(response.kpi.people)[0]

          this.tabs.people.date.from = dateFrom
          this.tabs.projects.date.from = dateFrom

          this.reports = response.kpi
          this.doneIssues = response.doneIssues
          this.worklogsOutsource = response.worklogsOutsource
          this.peopleAndProjectsInfo = response.peopleAndProjects
        })

        this.resetPagination()
      })
    },
    getReportDetailsByUser (dataRow, fieldUserName) {
      let mainArray, secondArray
      let arrayUserEstimations = []
      let arrayWorkSpentTime = []

      const user = dataRow[fieldUserName]
      const date = this.getCurrentTab.date.to

      const userEstimations = this.doneIssues[this.getCurrentTab.name][date] ? this.doneIssues[this.getCurrentTab.name][date][user] : null
      const workSpentTime = this.worklogsOutsource[this.getCurrentTab.name][date] ? this.worklogsOutsource[this.getCurrentTab.name][date][user] : null
      if (userEstimations) {
        arrayUserEstimations = this.convertObjectToArray(userEstimations['items'], 'keyWorklogs', 'valueWorklogs')
      }
      if (workSpentTime) {
        arrayWorkSpentTime = this.convertObjectToArray(workSpentTime['items'], 'keyOutsource', 'valueOutsource')
      }

      const countUserEstimations = arrayUserEstimations.length
      const countWorkSpentTime = arrayWorkSpentTime.length

      if (countUserEstimations >= countWorkSpentTime) {
        mainArray = arrayUserEstimations
        secondArray = arrayWorkSpentTime
      } else {
        mainArray = arrayWorkSpentTime
        secondArray = arrayUserEstimations
      }
      return this.connectObjectInArray(mainArray, secondArray)
    },
    connectObjectInArray (main, second) {
      return main.map((item, index) => {
        return {
          ...second[index],
          ...item
        }
      })
    },
    convertObjectToArray (data, keyName, valueName) {
      let result = []

      for (let key in data) {
        result.push({ [keyName]: key, [valueName]: data[key] })
      }

      return result
    },
    reportFilterByRole (report, filters) {
      return report.filter(item => item.roles.some(reportRole =>
        filters.some(filter => filter.name === reportRole)))
    },
    prepareDataForGetReports (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const fieldName = field['name']
        const fieldValue = fields[key]['value']
        const value = fieldValue.map(item => item[fields[key]['valueField']])

        data[fieldName] = value
      }

      return data
    },
    async clearCache () {
      this.$snotify.success(
        'Cache flushed successfully',
        'Success',
        this.$store.state.snotify
      )
      try {
        await this.$api.base.clearKPICache()
      } catch (error) {
        this.$snotify.error(
          error.response.data.message,
          'Error',
          this.$store.state.snotify
        )
        throw (error)
      }
    },

    async fetchAllUsers () {
      return this.$api.users.getAllUsers()
    },
    async fetchProjects () {
      return this.$api.projects.getProjects()
    },
    async fetchUnitManagers () {
      return this.$api.users.getUnitManagers()
    },
    fetchAllData () {
      return Promise.all([
        this.fetchAllUsers(),
        this.fetchProjects(),
        this.fetchUnitManagers()
      ]).then(() => {
        this.setAllOptionsFilter()
      })
    },
    setAllOptionsFilter () {
      this.filters.users.options = this.getAllUsers
      this.filters.projects.options = this.getProjects
      this.filters.unitManagers.options = this.getUnitManagers
    },
    getProjectDashboardKey (item) {
      return item.is_custom_project ? 'OUT' : item.key
    }
  }
}
</script>

<style lang="scss">

  .cursor-pointer {
    cursor: pointer;
  }

  .report-table .b-table-details thead{
      display: table-header-group;
  }
  .table-wrapper {
    padding-top: 20px;
    h3 {
      margin-bottom: 0;
    }
  }
  .table-wrapper-heading{
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 50px;
    .btn{
      cursor: pointer;
    }
  }
  .table {
      transition: transform 1s;
  }
  .block-loader {
      height: 85px;
  }
  .log_time_head {
      display: inline-block;
  }

  .form-generator_filter{
    form{
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
    }
    .form-generator__items{
      width: 900px;
    }
    .form-generator__controls{
      width: 520px;
      margin-left: 15px;
    }
    .columns-description{
      display: inline-block;
      margin-bottom: 0;
    }
    .btn{
      min-height: 43px;
      line-height: 29px;
    }
  }
  .filter-datepicker{
    display: flex;
    .vdp-datepicker{
      width: 120px;
      text-align: center;
      .input-group {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        &::after {
          content: '';
        }
      }
    }
    .form-control[readonly]{
      background-color: transparent;
    }
    .form-control{
      border: none;
      text-align: center;
    }
  }
  /*.filter-datepicker{*/
  /*  display: flex;*/
  /*  .vdp-datepicker{*/
  /*    width: 120px;*/
  /*    text-align: center;*/
  /*  }*/
  /*  .form-control[readonly]{*/
  /*    background-color: transparent;*/
  /*  }*/
  /*  .form-control{*/
  /*    border: none;*/
  /*    text-align: center;*/
  /*  }*/
  /*}*/
  /*.filter-switch{*/
  /*  .active{*/
  /*    color: #212529*/
  /*  }*/
  /*}*/
  /*.filter-switch__label{*/
  /*  color: gray;*/
  /*}*/
  /*.filters__row{*/
  /*  display: flex;*/
  /*  align-items: center;*/
  /*  margin-bottom: 20px;*/
  /*  &:last-child{*/
  /*    margin-bottom: 0;*/
  /*  }*/
  /*}*/
  /*.filters{*/
  /*  margin-bottom: 30px;*/
  /*  margin-top: 10px;*/
  /*}*/
  .column-act-outsource{
    width: 188px;
  }
 .disabled-more.column-more button {
    pointer-events: none;
    opacity: 0.5;
 }
</style>
